import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Radix Dialog wrapper themed to match the rest of the app. Replaces the
 * browser's native confirm / alert dialogs for every in-app
 * confirmation. See `ConfirmDialog` for the typical use.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  hideClose,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
  hideClose?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-800',
            'shadow-xl p-6 outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogPrimitive.Title className="text-base font-medium text-ink-900 dark:text-ink-100">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1.5 text-sm text-ink-600 dark:text-ink-300 leading-relaxed">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {!hideClose ? (
              <DialogPrimitive.Close
                className="p-1 rounded-md text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800"
                aria-label="Close"
              >
                <X size={16} />
              </DialogPrimitive.Close>
            ) : null}
          </div>
          {children ? <div className="mt-5">{children}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const DialogFooter = ({ children }: { children: ReactNode }) => (
  <div className="mt-6 flex flex-row-reverse items-center gap-2">{children}</div>
);
